import { isValid } from 'date-fns';
import { 
  useQuery, 
  UseQueryResult, 
  QueryKey 
} from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { User } from '@/types/user';
import type { TeamLogoSource } from '@/shared/services/getTeamLogoUrl';

// Define the form values interface with more precise typing
export interface ClipsFormValues {
  date?: Date | null;
  team?: string | null;
  clipType: 'all' | 'clip' | 'bookmark';
  status?: string | null;
}

/** Season the clip's game belongs to. Null for games imported without one. */
export interface ClipSeasonFromServer {
  id: number;
  name?: string | null;
  year?: string | null;
  /** 'Regular' | 'Post' | 'Pre' — the DB stores the long form. */
  seasonType?: string | null;
  /** Owning league, so a picker can scope to the user's current one. */
  leagueId?: number | null;
}

/** Public CloudFront URLs for the gpu-worker's derived preview assets. */
export interface ClipPreviewUrls {
  thumbnailUrl: string;
  webpUrl: string;
  mp4Url: string;
  gifUrl: string;
}

/** Tracked entity recorded on the clip's formatters snapshot. */
export interface ClipTrackedObject {
  type: 'Player' | 'BallOrPuck' | 'Manual' | 'Unknown';
  id?: number;
  displayName?: string;
  playerNumber?: string;
  playerPosition?: string;
  playerTeamId?: number | null;
}

/**
 * Subset of `clip.formatters` the API exposes. Event fields come from the
 * clip's play-by-play group, so they are null for group-less clips.
 */
export interface ClipFormattersFromServer {
  clipName?: string | null;
  eventName?: string | null;
  eventType?: string | null;
  period?: string | null;
  gameTime?: string | null;
  cameraName?: string | null;
  homeTeam?: string | null;
  awayTeam?: string | null;
  trackedObject?: ClipTrackedObject | null;
}

// Define the server response type with comprehensive type safety
export interface ClipModelFromServer {
  id: string;
  note: string;
  mediatype: 'clip' | 'bookmark';
  deleted: boolean;
  clouduser: User;
  startTimestamp: string;
  endTimestamp?: string;
  objectTrackingId?: number;
  cameraId?: number;
  cameraMode?: string;
  autoDirector?: boolean;
  preview?: ClipPreviewUrls | null;
  formatters?: ClipFormattersFromServer | null;
  /**
   * Roster portrait for `formatters.trackedObject`, resolved server-side at read
   * time. Null when the clip is untracked or the roster carries no headshot.
   */
  trackedPlayerHeadshot?: string | null;
  /** Origin of the clip's group: 'manual' | 'automated'. */
  source?: string;
  game: {
    id: string;
    uniqueId: string;
    awayTeam: {
      id: number;
      name: string;
      abbreviation: string;
      teamColors?:any;
      players?: any;
    } & TeamLogoSource;
    homeTeam: {
      id: number;
      name: string;
      abbreviation: string;
      teamColors?:any;
      players?: any;
    } & TeamLogoSource;
    season?: ClipSeasonFromServer | null;
    startTimestamp: string;
  };
  createdAt: string;
  updatedAt: string;
  status: 'READY' | 'PENDING' | 'ERROR' | 'CANCELED' | 'UNPROCESSED';
}

export type GetClipsResult = {
  rows: ClipModelFromServer[];
  rowCount: number;
};

/**
 * Authorship filter. 'me' and 'others' both mean a person, so a clip cut by a
 * rule is reachable only through 'automation'.
 */
export type ClipCreatorParam = 'me' | 'others' | 'automation';

// Comprehensive query params type
export type QueryParams = {
  page: number;
  sort: 'desc' | 'asc';
  sortField?: string;
  pageSize: number;
  textSearch?: string;
  /** Soft-delete state to return. Omitted returns both. */
  deleted?: boolean;

  // Clip library filters. Each is resolved server-side against a real column;
  // the option lists come from GET /v1.0/clips/facets.
  leagueId?: number;
  seasonId?: number;
  gameId?: number;
  /** SES tracked-object id, not our player.id. */
  playerId?: number;
  eventType?: string;
  creator?: ClipCreatorParam;
  /** Drop rule-cut clips whatever `creator` says — the flag gate, not a choice. */
  excludeAutomated?: boolean;
} & Partial<ClipsFormValues>;

// Improved query key generation with more predictable caching
export const getQueryKey = (
  params: QueryParams
): QueryKey => ['clips', params];

// Robust fetch clips function with improved error handling
export const fetchClips = async (params: QueryParams): Promise<GetClipsResult> => {
  try {
    // Create a copy to avoid mutating the original params
    const searchParams = new URLSearchParams();
    
    // Pagination
    searchParams.set('page', params.page.toString());
    searchParams.set('pageSize', params.pageSize.toString());
    
    // Sorting
    searchParams.set('sortDirection', params.sort);
    searchParams.set('sortField', params.sortField || 'createdAt');
    
    // Optional filters with null/undefined checks
    if (params.team) {
      searchParams.set('team', params.team);
    }
    
    if (params.textSearch) {
      searchParams.set('text', params.textSearch);
    }
    
    if (params.date && isValid(params.date)) {
      searchParams.set('date', params.date.toISOString());
    }
    
    if (params.status) {
      searchParams.set('status', params.status);
    }

    // Explicit undefined check: `deleted=false` is the meaningful case, so a
    // falsy test would drop it.
    if (params.deleted !== undefined) {
      searchParams.set('deleted', String(params.deleted));
    }

    // Library filters. Omitted rather than sent empty — the API reads a missing
    // param as "no filter", so there is no "all" value to encode.
    if (params.leagueId) {
      searchParams.set('leagueId', String(params.leagueId));
    }

    if (params.seasonId) {
      searchParams.set('seasonId', String(params.seasonId));
    }

    if (params.gameId) {
      searchParams.set('gameId', String(params.gameId));
    }

    if (params.playerId) {
      searchParams.set('playerId', String(params.playerId));
    }

    if (params.eventType) {
      searchParams.set('eventType', params.eventType);
    }

    if (params.creator) {
      searchParams.set('creator', params.creator);
    }

    if (params.excludeAutomated) {
      searchParams.set('excludeAutomated', 'true');
    }

    // Always set clip type with a default
    searchParams.set('clipType', params.clipType || 'all');
    
    // Fetch with error handling
    const response = await get<GetClipsResult>(
      `/v1.0/clips?${searchParams.toString()}`
    );

    return response.data;

  } catch (error) {
    console.error('Failed to fetch clips:', error);
    throw error;
  }
};

// Enhanced query hook with better typing and configuration
const useAllClipsQuery = (
  formValues: ClipsFormValues,
  textSearch = '',
  pageSize = 10,
  page = 0,
  sort = 'desc',
  sortField = 'createdAt',
  refetchInterval = 0, // ms; 0 = no polling
): UseQueryResult<GetClipsResult, Error> => {
  // Combine all parameters for query key and fetching
  const queryParams: QueryParams = {
    page,
    pageSize,
    textSearch,
    sort: sort as 'desc' | 'asc',
    sortField,
    ...formValues
  };

  return useQuery({
    // Generate a stable, comprehensive query key
    queryKey: getQueryKey(queryParams),
    
    // Async query function with full parameters
    queryFn: () => fetchClips(queryParams),
    
    // Advanced caching and refetching strategies
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30,   // 30 minutes

    // Keep previous data for smooth UX during loading
    placeholderData: (previousData) => previousData,
    
    // Retry mechanism
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    
    // Error handling
    throwOnError: false,
    
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: refetchInterval || false,
  });
};

export default useAllClipsQuery;